import {
  createEventEntry,
  createHoleScore,
  getLayoutHoles,
  createThrowScore,
} from "./db";

/**
 * Registers a player for an event by creating event entry, hole scores, and throw scores
 * @param {Object} registrationData - The registration data
 * @param {string} registrationData.event_id - The event ID
 * @param {string} registrationData.user_id - The user ID
 * @param {string} registrationData.event_divisions_id - The division ID
 * @param {string} registrationData.layout_id - The layout ID
 * @param {number} registrationData.numThrows - Number of throws per hole (default: 2)
 * @returns {Promise<Object>} The created event entry data
 */
export async function registerPlayerForEvent(registrationData) {
  const {
    event_id,
    user_id,
    event_divisions_id,
    layout_id,
    numThrows = 2,
  } = registrationData;

  try {
    // Step 1: Create event entry
    const registerData = await createEventEntry({
      event_id,
      user_id,
      event_divisions_id,
    });

    // Step 2: Get layout holes
    const layoutData = await getLayoutHoles(layout_id);

    // Step 3: Create all hole scores in parallel
    const holeScorePromises = layoutData.map((hole) =>
      createHoleScore({
        layout_holes_id: hole.layout_holes_id,
        event_entries_id: registerData[0].id,
      })
    );

    const holeScoreResults = await Promise.all(holeScorePromises);

    // Step 4: Create all throw scores in parallel
    const throwScorePromises = holeScoreResults.flatMap((holeScoreData) => {
      const promises = [];
      for (let throwNumber = 1; throwNumber <= numThrows; throwNumber++) {
        promises.push(
          createThrowScore({
            hole_scores_id: holeScoreData[0].hole_scores_id,
            throw_number: throwNumber,
          })
        );
      }
      return promises;
    });

    await Promise.all(throwScorePromises);

    // Return the event entry data for further use
    return registerData[0];
  } catch (error) {
    console.error("Error during player registration:", error);
    throw new Error(`Registration failed: ${error.message}`);
  }
}
